import type { Configuration, PopupRequest, SilentRequest } from '@azure/msal-browser';
import { LogLevel } from '@azure/msal-browser';

const backendClientId = process.env.REACT_APP_ENTRAID_BACKEND_CLIENTID;
const frontendClientId = process.env.REACT_APP_ENTRAID_FRONTEND_CLIENTID;
const backendScope = process.env.REACT_APP_ENTRAID_BACKEND_SCOPE;
export const skllsTenantId = process.env.REACT_APP_ENTRAID_TENANTID;

export const guestMsalConfig: Configuration = {
  auth: {
    clientId: frontendClientId,
    authority: `https://login.microsoftonline.com/${skllsTenantId}`,
    redirectUri: '/'
  }
};

export const backendMsalConfig: Configuration = {
  auth: {
    clientId: backendClientId,
    authority: 'https://login.microsoftonline.com/organizations',
    redirectUri: '/'
  }
};

export const frontendMsalConfig: Configuration = {
  auth: {
    clientId: frontendClientId,
    authority: `https://login.microsoftonline.com/organizations`,
    redirectUri: '/'
  }
};

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: frontendClientId,
    authority: 'https://login.microsoftonline.com/organizations',
    redirectUri: '/'
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: true, // Enable for better cross-tab support and stability
    secureCookies: window.location.protocol === 'https:'
  },
  system: {
    tokenRenewalOffsetSeconds: 600, // Refresh tokens 10 minutes before expiry (increased for better UX)
    allowNativeBroker: false, // Disable for web apps
    windowHashTimeout: 60000, // Increase timeout for slower networks
    iframeHashTimeout: 10000, // Timeout for iframe-based silent requests
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            // console.info(message);
            return;
          case LogLevel.Verbose:
            // console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
          default:
            return;
        }
      }
    }
  }
};

export const skllsMsalConfig = {
  ...msalConfig,
  auth: guestMsalConfig.auth
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest: PopupRequest = {
  scopes: ['User.Read', 'offline_access', backendScope], // offline_access enables refresh tokens for longer sessions
  redirectUri: '/blank.html'
};

/**
 * Constants for logout state management
 */
export const LOGOUT_FLAG_KEY = 'shouldShowSelector';

/**
 * Check if user recently logged out explicitly
 */
export const hasUserLoggedOut = (): boolean => {
  const flagValue = localStorage.getItem(LOGOUT_FLAG_KEY);
  return flagValue === 'true';
};

/**
 * Set logout flag when user explicitly logs out
 */
export const setLogoutFlag = (): void => {
  localStorage.setItem(LOGOUT_FLAG_KEY, 'true');
};

/**
 * Clear logout flag after successful login
 */
export const clearLogoutFlag = (): void => {
  localStorage.removeItem(LOGOUT_FLAG_KEY);
};

/**
 * Get login request with dynamic prompt based on logout state and account count
 * @param accountCount Number of available accounts
 * @param targetAccount Specific account to use (for single account scenarios)
 */
export const getDynamicLoginRequest = (accountCount: number = 0, targetAccount?: any): PopupRequest => {
  // If user explicitly logged out, always show account selection
  if (hasUserLoggedOut()) {
    return {
      ...loginRequest,
      prompt: 'select_account'
    };
  }
  
  // For returning users try to use the first account
  if (targetAccount) {
    return {
      ...loginRequest,
      prompt: 'none',
      account: targetAccount
    };
  }
  
  // For multiple accounts or no specific account, show selection
  return {
    ...loginRequest,
    prompt: 'select_account'
  };
};

/**
 * Check if we should attempt silent authentication for returning users
 */
export const shouldAttemptSilentAuth = (): boolean => {
  return !hasUserLoggedOut();
};

export const apiLoginRequest: SilentRequest = {
  scopes: ['offline_access', backendScope] // offline_access enables refresh tokens for longer sessions
};

export const graphLoginRequest: SilentRequest = {
  scopes: ['User.Read']
};

/**
 * Add here the scopes to request when obtaining an access token for MS Graph API. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const graphConfig = {
  graphMeEndpoint: 'https://graph.microsoft.com/v1.0/me'
};
